package com.example.mystybag.appCore.views.addMerchantLocation

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.addMerchantLocation.MENU_PHOTO_ASPECT_RATIO
import com.example.mystybag.appCore.logic.addMerchantLocation.MerchantRegistrationAddMerchantLocationDetailsViewModel
import com.example.mystybag.appCore.logic.addMerchantLocation.MAX_MENU_PHOTOS
import com.example.mystybag.appCore.logic.addMerchantLocation.MENU_PHOTOS_GRID_COLUMNS
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogCategoryDraft
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogEditorKind
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogEditorState
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogProductDraft
import com.example.mystybag.appCore.model.addMerchantLocation.LocationDetailsRequiredField
import com.example.mystybag.appCore.model.addMerchantLocation.MerchantLocationTab
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.CheckIcon
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.custom.ImageDescriptionView
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.model.ImageDescription
import com.example.mystybag.core.model.IconDescription
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.ColorDefines
import com.example.mystybag.core.views.SharedColor
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

private val AccordionHeaderHeight = 56.dp

// Web: location map slot is 15% shorter than the computed max so Menu /
// Allergens stay easier to reach without scrolling as far.
private const val WebLocationMapHeightScale = 0.85f

@Composable
private fun LocationAccordionSectionHeader(
    tab: MerchantLocationTab,
    label: String,
    icon: ImageResource,
    expanded: Boolean,
    tabsWithErrors: Set<MerchantLocationTab>,
    onTabSelected: (MerchantLocationTab) -> Unit
) {
    val colors = AppTheme.current.colors
    val primaryVariant = colors.inversePrimary
    val hasError = tab in tabsWithErrors
    val titleColor = when {
        hasError -> colors.error
        expanded -> primaryVariant
        else -> colors.onSurface
    }
    val iconColor = when {
        hasError -> colors.error
        expanded -> primaryVariant
        else -> colors.onSurfaceVariant
    }
    val chevronColor = when {
        hasError -> colors.error
        else -> primaryVariant
    }
    val arrowRotation by animateFloatAsState(
        targetValue = if (expanded) 180f else 0f,
        animationSpec = tween(durationMillis = 200),
        label = "accordionArrowRotation"
    )
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(AccordionHeaderHeight)
            .clip(RoundedCornerShape(p20))
            .background(colors.surfaceContainerLowest)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null
            ) { onTabSelected(tab) }
            .padding(horizontal = p16),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            painter = painterResource(icon),
            contentDescription = null,
            modifier = Modifier.size(24.dp),
            colorFilter = ColorFilter.tint(iconColor)
        )
        Spacer(modifier = Modifier.width(p12))
        Texts.TitleMedium(
            text = label,
            color = titleColor,
            modifier = Modifier
                .weight(1f)
                .padding(end = p8)
        )
        Image(
            painter = painterResource(SRC.images.combobox_arrow_down_green),
            contentDescription = null,
            modifier = Modifier
                .size(16.dp)
                .graphicsLayer { rotationZ = arrowRotation },
            colorFilter = ColorFilter.tint(chevronColor)
        )
    }
}

@Composable
fun MerchantRegistrationAddMerchantLocationDetailsView(viewModel: MerchantRegistrationAddMerchantLocationDetailsViewModel) {
    val state by viewModel.uiState.subscribeAsState()
    val density = LocalDensity.current
    var businessCategoryFieldWidth by remember { mutableStateOf(0.dp) }
    val storeNameFocusRequester = remember { FocusRequester() }

    LaunchedEffect(state.validationRequestId) {
        if (state.validationRequestId == 0) return@LaunchedEffect
        state.validationTargetTab?.let(viewModel::selectTab)
        when (state.focusTargetField) {
            LocationDetailsRequiredField.STORE_NAME -> storeNameFocusRequester.requestFocus()
            LocationDetailsRequiredField.CITY -> viewModel.selectTab(MerchantLocationTab.LOCATION)
            else -> Unit
        }
    }

    val tabsWithErrors = remember(state.requiredFieldErrors, state.requiredProductNameRows, state.requiredProductGramsRows) {
        buildSet {
            if (LocationDetailsRequiredField.STORE_NAME in state.requiredFieldErrors) {
                add(MerchantLocationTab.BUSINESS)
            }
            if (
                LocationDetailsRequiredField.CITY in state.requiredFieldErrors ||
                LocationDetailsRequiredField.MAP_LOCATION in state.requiredFieldErrors
            ) add(MerchantLocationTab.LOCATION)
            if (
                LocationDetailsRequiredField.BUSINESS_CATEGORY in state.requiredFieldErrors ||
                LocationDetailsRequiredField.COVER_PREVIEW in state.requiredFieldErrors ||
                state.requiredProductNameRows.isNotEmpty() ||
                state.requiredProductGramsRows.isNotEmpty()
            ) add(MerchantLocationTab.BUSINESS)
        }
    }

    if (rememberIsWebDesktopLayout()) {
        UIView(
            viewModel = viewModel,
            modifier = Modifier.fillMaxSize(),
            showTopBar = false,
            removeVerticalPadding = true
        ) {
            Column(modifier = Modifier.fillMaxSize()) {
                ClientWebHeaderBar(
                    selectedTab = 3,
                    homeTabIndex = 0,
                    deliveryTabIndex = 1,
                    favoritesTabIndex = 2,
                    menuTabIndex = 3,
                    searchCallbacks = null,
                    onFilterClick = {},
                    showSearchAndFilter = false,
                    showBackButton = true,
                    onBackClick = { viewModel.navCallbacks.backButtonCallback() },
                    centerTitle = sharedString(SR.strings.object_screen_title)
                )
                WebHeaderBottomDivider()
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    contentAlignment = Alignment.TopCenter
                ) {
                    MerchantRegistrationLocationDetailsContent(
                        state = state,
                        viewModel = viewModel,
                        tabsWithErrors = tabsWithErrors,
                        density = density,
                        storeNameFocusRequester = storeNameFocusRequester,
                        modifier = Modifier
                            .width(1080.dp)
                            .fillMaxHeight()
                            .padding(horizontal = p16, vertical = p8)
                    )
                }
            }
        }
    } else {
        UIView(viewModel = viewModel, horizontalAlignment = Alignment.CenterHorizontally) {
            MerchantRegistrationLocationDetailsContent(
                state = state,
                viewModel = viewModel,
                tabsWithErrors = tabsWithErrors,
                density = density,
                storeNameFocusRequester = storeNameFocusRequester,
                modifier = Modifier.fillMaxSize()
            )
        }
    }
}

@Composable
private fun MerchantRegistrationLocationDetailsContent(
    state: com.example.mystybag.appCore.model.addMerchantLocation.MerchantRegistrationAddMerchantLocationDetailsUIState,
    viewModel: MerchantRegistrationAddMerchantLocationDetailsViewModel,
    tabsWithErrors: Set<MerchantLocationTab>,
    density: androidx.compose.ui.unit.Density,
    storeNameFocusRequester: FocusRequester,
    modifier: Modifier
) {
    var businessCategoryFieldWidth by remember { mutableStateOf(0.dp) }
    val expandedTab = state.expandedTab
    // Desktop (wide Web) layout flag; false on native and on portrait Web, so the form
    // renders like the native mobile UI there.
    val isWebDesktop = rememberIsWebDesktopLayout()
    data class AccordionSection(
        val tab: MerchantLocationTab,
        val label: String,
        val icon: ImageResource
    )
    Column(
        modifier = modifier
    ) {
        val scrollState = rememberScrollState()
        val accordionSections = listOf(
            AccordionSection(MerchantLocationTab.BUSINESS, stringResource(SR.strings.business_details), SRC.images.location_tab_business_icon),
            AccordionSection(MerchantLocationTab.PRODUCTS, stringResource(SR.strings.products_section_title), SRC.images.ic_restaurant_menu),
            AccordionSection(MerchantLocationTab.LOCATION, stringResource(SR.strings.tab_location_short), SRC.images.location_tab_location_icon),
            AccordionSection(MerchantLocationTab.MENU, stringResource(SR.strings.menu_photos_section_title), SRC.images.camera_1)
        )
        // When Location is expanded, show Menu under the map so the viewport can fit exactly one section row (Menu).
        val orderedAccordionSections =
            if (expandedTab == MerchantLocationTab.LOCATION) {
                accordionSections.filter {
                    it.tab != MerchantLocationTab.MENU
                } + accordionSections.filter { it.tab == MerchantLocationTab.MENU }
            } else {
                accordionSections
            }

        BoxWithConstraints(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
        ) {
            val scrollViewportHeight = maxHeight
            val heightBeforeLocationMap =
                AccordionHeaderHeight + p8 + AccordionHeaderHeight + p12
            // Keep at least the next two accordion headers ("Menu" and
            // "Allergens") visible when Location is expanded on Web.
            // Android/iOS keep the previous sizing logic.
            val locationMapReservedHeaders =
                if (isWebDesktop && expandedTab == MerchantLocationTab.LOCATION) {
                    (AccordionHeaderHeight * 2) + p8
                } else {
                    AccordionHeaderHeight
                }
            val locationMapHeightBase =
                (scrollViewportHeight - heightBeforeLocationMap - p20 - locationMapReservedHeaders)
                    .coerceAtLeast(200.dp)
            val locationMapHeight =
                if (isWebDesktop && expandedTab == MerchantLocationTab.LOCATION) {
                    locationMapHeightBase * WebLocationMapHeightScale
                } else {
                    locationMapHeightBase
                }

            Column(
                modifier = Modifier
                    .height(scrollViewportHeight)
                    .fillMaxWidth()
                    .verticalScroll(scrollState)
                    .padding(top = p20)
            ) {
                orderedAccordionSections.forEach { (tab, label, icon) ->
                Box(modifier = Modifier.padding(horizontal = p16)) {
                    LocationAccordionSectionHeader(
                        tab = tab,
                        label = label,
                        icon = icon,
                        expanded = expandedTab == tab,
                        tabsWithErrors = tabsWithErrors,
                        onTabSelected = viewModel::toggleTab
                    )
                }

                if (expandedTab == tab) {
                    Spacer(modifier = Modifier.height(p12))
                    // The mobile design intentionally renders the LOCATION map edge-to-edge
                    // (0.dp horizontal padding). On Web that visual choice clashes with the
                    // bounded ~1080dp form column — the map clearly looks 16dp wider on each
                    // side than the accordion headers above/below it. Add the standard p16
                    // padding on Web only, so the map aligns with the rest of the column.
                    val locationContentHorizontalPadding =
                        if (tab == MerchantLocationTab.LOCATION) {
                            if (isWebDesktop) p16 else 0.dp
                        } else {
                            p16
                        }
                    val contentModifier = if (tab == MerchantLocationTab.LOCATION) {
                        Modifier
                            .fillMaxWidth()
                            .padding(horizontal = locationContentHorizontalPadding)
                    } else {
                        Modifier
                            .fillMaxWidth()
                            .padding(horizontal = p16)
                    }
                    Column(modifier = contentModifier) {
            when (expandedTab) {
                    // Legacy enum value kept for compatibility; this tab is no longer rendered in the accordion.
                    MerchantLocationTab.DETAILS -> Unit
                    MerchantLocationTab.LOCATION -> {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(locationMapHeight)
                                .clip(RoundedCornerShape(topStart = p20, topEnd = p20))
                                .border(
                                    width = if (LocationDetailsRequiredField.MAP_LOCATION in state.requiredFieldErrors) p2 else p1,
                                    color = if (LocationDetailsRequiredField.MAP_LOCATION in state.requiredFieldErrors) AppTheme.current.colors.error else AppTheme.current.colors.outline,
                                    shape = RoundedCornerShape(topStart = p20, topEnd = p20)
                                )
                        ) {
                            MerchantRegistrationReviewStoreDetailsMapView(
                                modifier = Modifier.fillMaxSize(),
                                latitude = state.latitude,
                                longitude = state.longitude,
                                onLocationChanged = { lat, lng -> viewModel.updateLocation(lat, lng) },
                                onMapReady = viewModel::onMapReady,
                                searchPlaceHolder = stringResource(SR.strings.merchant_location_map_search_hint)
                            )
                        }
                    }

                    MerchantLocationTab.BUSINESS -> {
                        DefaultCircleShapedTextField(
                            value = state.storeName,
                            onValueChange = viewModel::updateStoreName,
                            label = stringResource(SR.strings.store_name),
                            modifier = Modifier.fillMaxWidth(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                            isError = LocationDetailsRequiredField.STORE_NAME in state.requiredFieldErrors,
                            errorText = "",
                            focusRequester = storeNameFocusRequester
                        )
                        Spacer(modifier = Modifier.height(p24))
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { viewModel.showBusinessCategoryPicker() }
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .onGloballyPositioned { coordinates ->
                                        businessCategoryFieldWidth = with(density) { coordinates.size.width.toDp() }
                                    }
                            ) {
                                DefaultCircleShapedTextField(
                                    value = state.selectedBusinessCategory?.title ?: "",
                                    onValueChange = {},
                                    label = stringResource(SR.strings.select_store_type),
                                    modifier = Modifier.fillMaxWidth(),
                                    readOnly = true,
                                    isError = LocationDetailsRequiredField.BUSINESS_CATEGORY in state.requiredFieldErrors,
                                    errorText = "",
                                    backgroundColor = AppTheme.current.colors.surface,
                                    borderColor = AppTheme.current.colors.outline,
                                    trailingIcon = {
                                        Image(
                                            painter = painterResource(SRC.images.combobox_arrow_down_green),
                                            contentDescription = sharedString(SR.strings.select_business_category),
                                            contentScale = ContentScale.Fit,
                                            modifier = Modifier.size(p16),
                                            colorFilter = ColorFilter.tint(
                                                if (LocationDetailsRequiredField.BUSINESS_CATEGORY in state.requiredFieldErrors) {
                                                    AppTheme.current.colors.error
                                                } else {
                                                    AppTheme.current.colors.primary
                                                }
                                            )
                                        )
                                    }
                                )
                                Box(modifier = Modifier.matchParentSize().clickable { viewModel.showBusinessCategoryPicker() })
                                DropdownMenu(
                                    expanded = state.showBusinessCategoryPicker,
                                    onDismissRequest = viewModel::hideBusinessCategoryPicker,
                                    containerColor = AppTheme.current.colors.surface,
                                    shape = RoundedCornerShape(p20),
                                    offset = DpOffset(x = 0.dp, y = 4.dp),
                                    modifier = Modifier.then(
                                        if (businessCategoryFieldWidth > 0.dp) Modifier.width(businessCategoryFieldWidth) else Modifier
                                    ).height(p40 * 8)
                                ) {
                                    state.businessCategories.forEachIndexed { index, category ->
                                        val isSelected = state.selectedBusinessCategory?.id == category.id
                                        DropdownMenuItem(
                                            text = {
                                                Texts.BodyLarge(
                                                    text = category.title,
                                                    color = if (isSelected) AppTheme.current.colors.primary else AppTheme.current.colors.onSurface
                                                )
                                            },
                                            onClick = { viewModel.selectBusinessCategory(category.id, category.title) },
                                            trailingIcon = {
                                                if (isSelected) {
                                                    CheckIcon(
                                                        contentDescription = sharedString(SR.strings.selected),
                                                        tint = AppTheme.current.colors.primary
                                                    )
                                                }
                                            }
                                        )
                                        if (index < state.businessCategories.lastIndex) {
                                            HorizontalDivider(thickness = 1.dp, color = AppTheme.current.colors.outline)
                                        }
                                    }
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(p16))
                        val coverUrls = state.selectedBusinessCategory?.coverImageUrls ?: emptyList()
                        if (coverUrls.isNotEmpty()) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .horizontalScroll(rememberScrollState()),
                                horizontalArrangement = Arrangement.spacedBy(p8)
                            ) {
                                coverUrls.forEachIndexed { index, url ->
                                    CoverPresetUrlCard(
                                        imageUrl = url,
                                        isSelected = state.selectedPreviewImageIndex == index,
                                        onClick = { viewModel.selectPreviewImage(index) },
                                        modifier = Modifier.width(112.dp)
                                    )
                                }
                            }
                        }
                        if (LocationDetailsRequiredField.COVER_PREVIEW in state.requiredFieldErrors) {
                            Texts.BodySmall(
                                text = stringResource(SR.strings.please_select_business_cover_image),
                                color = AppTheme.current.colors.error,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = p8)
                            )
                        }
                        Spacer(modifier = Modifier.height(p48))
                    }

                    MerchantLocationTab.PRODUCTS -> {
                        // The full product catalog (categories / subcategories /
                        // products) is edited inline here as a local draft tree and
                        // persisted to the server when the location is saved.
                        ProductsCatalogSection(state = state, viewModel = viewModel)
                        Spacer(modifier = Modifier.height(p48))
                    }

                    MerchantLocationTab.MENU -> {
                        val existingItems = state.existingMenuPhotoUrls
                        val newItems = state.menuPhotoFileNames
                        val totalItemsCount = existingItems.size + newItems.size
                        val canAdd = totalItemsCount < MAX_MENU_PHOTOS
                        val allSlots: List<Pair<String, String?>> =
                            existingItems.mapIndexed { index, url -> "existing:$index" to url } +
                                newItems.mapIndexed { index, fileName -> "new:$index" to fileName } +
                                if (canAdd) listOf("add" to null) else emptyList()
                        val rows = allSlots.chunked(MENU_PHOTOS_GRID_COLUMNS)

                        Column(modifier = Modifier.fillMaxWidth()) {
                            Texts.BodyMedium(
                                text = stringResource(SR.strings.menu_photos_up_to_10),
                                textAlign = TextAlign.Center,
                                modifier = Modifier.fillMaxWidth(),
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                            Spacer(modifier = Modifier.height(p12))
                            Column(verticalArrangement = Arrangement.spacedBy(p6)) {
                            rows.forEach { rowSlots ->
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(p6)
                                ) {
                                    rowSlots.forEach { slot ->
                                        val slotKey = slot.first
                                        val value = slot.second
                                        val isAddSlot = value == null
                                        Box(
                                            modifier = Modifier
                                                .weight(1f)
                                                .aspectRatio(MENU_PHOTO_ASPECT_RATIO)
                                                .clip(RoundedCornerShape(if (isAddSlot) p16 else p8))
                                                .then(
                                                    if (isAddSlot) {
                                                        Modifier
                                                    } else {
                                                        Modifier.border(p1, AppTheme.current.colors.outline, RoundedCornerShape(p8))
                                                    }
                                                )
                                        ) {
                                            if (value != null) {
                                                val imagePath = when {
                                                    slotKey.startsWith("existing:") -> value
                                                    else -> com.example.mystybag.cropImage.expected.getTempImagePath(value)
                                                }
                                                ImageDescriptionView(
                                                    modifier = Modifier.fillMaxSize(),
                                                    image = ImageDescription(link = imagePath, icon = null),
                                                    contentScale = ContentScale.Crop,
                                                )
                                                Box(
                                                    modifier = Modifier
                                                        .align(Alignment.TopEnd)
                                                        .padding(p4)
                                                        .size(32.dp)
                                                        .clip(CircleShape)
                                                        .background(AppTheme.current.colors.error)
                                                        .clickable {
                                                            when {
                                                                slotKey.startsWith("existing:") -> {
                                                                    val index = slotKey.substringAfter("existing:").toIntOrNull()
                                                                    if (index != null) viewModel.removeExistingMenuPhotoAt(index)
                                                                }

                                                                slotKey.startsWith("new:") -> {
                                                                    val index = slotKey.substringAfter("new:").toIntOrNull()
                                                                    if (index != null) viewModel.removeMenuPhotoAt(index)
                                                                }
                                                            }
                                                        },
                                                    contentAlignment = Alignment.Center
                                                ) {
                                                    Image(
                                                        painter = painterResource(SRC.images.mol_trash_icon),
                                                        contentDescription = stringResource(SR.strings.remove_photo),
                                                        modifier = Modifier.size(16.dp)
                                                    )
                                                }
                                            } else {
                                                Box(
                                                    modifier = Modifier
                                                        .fillMaxSize()
                                                        .clip(RoundedCornerShape(p16))
                                                        .background(AppTheme.current.colors.primaryContainer)
                                                        .clickable(enabled = slotKey == "add") { viewModel.onAddMenuPhotoClicked() },
                                                    contentAlignment = Alignment.Center
                                                ) {
                                                    Column(
                                                        horizontalAlignment = Alignment.CenterHorizontally,
                                                        verticalArrangement = Arrangement.Center,
                                                        modifier = Modifier.padding(horizontal = p4, vertical = p4)
                                                    ) {
                                                        ImageDescriptionView(
                                                            modifier = Modifier.size(p24),
                                                            image = ImageDescription(
                                                                link = null,
                                                                icon = IconDescription(
                                                                    resource = SRC.images.camera_1,
                                                                    imageSize = p24,
                                                                    background = SharedColor(
                                                                        color = ColorDefines.Surface,
                                                                        alpha = 0f
                                                                    )
                                                                )
                                                            )
                                                        )
                                                        Spacer(modifier = Modifier.height(p4))
                                                        Texts.LabelSmall(
                                                            text = stringResource(SR.strings.add_menu_photo),
                                                            color = AppTheme.current.colors.primary,
                                                            textAlign = TextAlign.Center,
                                                            maxLines = 3
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    repeat(MENU_PHOTOS_GRID_COLUMNS - rowSlots.size) { Spacer(modifier = Modifier.weight(1f)) }
                                }
                            }
                            }
                        }

                        if (state.errorMessage != null) {
                            Spacer(modifier = Modifier.height(p8))
                            Texts.BodyMedium(
                                text = state.errorMessage ?: "",
                                color = AppTheme.current.colors.error,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                    null -> Unit
            }
                    }
                    Spacer(modifier = Modifier.height(p20))
                } else {
                    Spacer(modifier = Modifier.height(p8))
                }
            }
            }
        }

        if (isWebDesktop) {
            // Match Objects list "Add Location": half width within max 412.dp column (MerchantLocationsListView).
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16, vertical = p8),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .widthIn(max = 412.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth(0.5f)
                            .height(p56),
                        fillMaxWidth = false,
                        type = DefaultButtonType.PRIMARY,
                        caption = if (state.isEditMode) stringResource(SR.strings.save) else stringResource(SR.strings.add),
                        capitalization = CaptionCapitalization.None,
                        contentPadding = PaddingValues(horizontal = p16, vertical = 0.dp),
                        callback = viewModel::onContinue
                    )
                }
            }
        } else {
            DefaultButton(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16, vertical = p8),
                type = DefaultButtonType.PRIMARY,
                caption = if (state.isEditMode) stringResource(SR.strings.save) else stringResource(SR.strings.add),
                callback = viewModel::onContinue
            )
        }
    }
}

@Composable
private fun CoverPresetUrlCard(
    imageUrl: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .aspectRatio(121f / 162f)
            .clip(RoundedCornerShape(p28))
            .border(
                width = if (isSelected) p2 else p1,
                color = if (isSelected) AppTheme.current.colors.primary else AppTheme.current.colors.outline.copy(alpha = 0.16f),
                shape = RoundedCornerShape(p28)
            )
            .clickable(onClick = onClick)
    ) {
        ImageDescriptionView(
            modifier = Modifier.fillMaxSize(),
            image = ImageDescription(link = imageUrl, icon = null)
        )
    }
}

// region Product catalog (inline draft tree)

private val CatalogCardCornerRadius = p16
private val CatalogGroupActionButtonShape = RoundedCornerShape(50.dp)
private val CatalogActionButtonHeight = 55.dp
private val CatalogIconActionSize = 40.dp

@Composable
private fun ProductsCatalogSection(
    state: com.example.mystybag.appCore.model.addMerchantLocation.MerchantRegistrationAddMerchantLocationDetailsUIState,
    viewModel: MerchantRegistrationAddMerchantLocationDetailsViewModel
) {
    val editor = state.catalogEditor
    val rootCategories = state.catalogCategories
        .filter { it.parentLocalId == null }
        .sortedBy { it.sortOrder }
    val addingRootCategory = editor != null &&
        editor.kind == CatalogEditorKind.CATEGORY &&
        editor.parentLocalId == null &&
        editor.editingLocalId == null

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p16)
    ) {
        if (addingRootCategory && editor != null) {
            CatalogInlineNameEditor(editor = editor, viewModel = viewModel)
        } else {
            CatalogAddNewGroupButton(onClick = { viewModel.catalogStartAddCategory(null) })
        }

        if (rootCategories.isEmpty() && !addingRootCategory) {
            CatalogEmptyState()
        } else {
            rootCategories.forEach { category ->
                CatalogCategoryNode(category = category, state = state, viewModel = viewModel, depth = 0)
            }
        }
    }
}

@Composable
private fun CatalogAddNewGroupButton(onClick: () -> Unit) {
    val colors = AppTheme.current.colors
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(CatalogActionButtonHeight)
            .clip(RoundedCornerShape(CatalogCardCornerRadius))
            .background(colors.primaryContainer)
            .border(p1, colors.primary, RoundedCornerShape(CatalogCardCornerRadius))
            .clickable(onClick = onClick)
            .padding(horizontal = p16),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            painter = painterResource(SRC.images.ic_plus_icon),
            contentDescription = null,
            modifier = Modifier.size(20.dp),
            colorFilter = ColorFilter.tint(colors.primary)
        )
        Spacer(modifier = Modifier.width(p8))
        Texts.BodyLarge(
            text = stringResource(SR.strings.add_new_group),
            color = colors.primary
        )
    }
}

@Composable
private fun CatalogCategoryNode(
    category: CatalogCategoryDraft,
    state: com.example.mystybag.appCore.model.addMerchantLocation.MerchantRegistrationAddMerchantLocationDetailsUIState,
    viewModel: MerchantRegistrationAddMerchantLocationDetailsViewModel,
    depth: Int
) {
    val colors = AppTheme.current.colors
    val editor = state.catalogEditor
    val expanded = category.localId in state.catalogExpandedLocalIds
    val childCategories = state.catalogCategories
        .filter { it.parentLocalId == category.localId }
        .sortedBy { it.sortOrder }
    val childProducts = state.catalogProducts
        .filter { it.categoryLocalId == category.localId }
        .sortedBy { it.sortOrder }
    val isEditingThisCategory = editor != null &&
        editor.kind == CatalogEditorKind.CATEGORY &&
        editor.editingLocalId == category.localId
    val addingSubcategoryHere = editor != null &&
        editor.kind == CatalogEditorKind.CATEGORY &&
        editor.parentLocalId == category.localId &&
        editor.editingLocalId == null
    val addingProductHere = editor != null &&
        editor.kind == CatalogEditorKind.PRODUCT &&
        editor.parentLocalId == category.localId &&
        editor.editingLocalId == null

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(CatalogCardCornerRadius))
            .background(colors.surface)
            .border(p1, colors.outline, RoundedCornerShape(CatalogCardCornerRadius))
            .padding(p16),
        verticalArrangement = Arrangement.spacedBy(p12)
    ) {
        if (isEditingThisCategory && editor != null) {
            CatalogInlineNameEditor(
                editor = editor,
                viewModel = viewModel
            )
        } else {
            CatalogCategoryHeader(
                name = category.name,
                expanded = expanded,
                subcategoryCount = childCategories.size,
                productCount = childProducts.size,
                isSubGroup = depth > 0,
                onToggle = { viewModel.catalogToggleExpanded(category.localId) },
                onEdit = { viewModel.catalogStartEditCategory(category.localId) },
                onDelete = { viewModel.catalogDeleteCategory(category.localId) }
            )
        }

        if (expanded) {
            if (depth == 0) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(p8)
                ) {
                    CatalogFilledActionButton(
                        label = stringResource(SR.strings.catalog_add_subgroup),
                        onClick = { viewModel.catalogStartAddCategory(category.localId) }
                    )
                    CatalogContainerActionButton(
                        label = stringResource(SR.strings.catalog_add_product),
                        onClick = { viewModel.catalogStartAddProduct(category.localId) }
                    )
                }
            } else {
                CatalogContainerActionButton(
                    label = stringResource(SR.strings.catalog_add_product),
                    onClick = { viewModel.catalogStartAddProduct(category.localId) }
                )
            }

            if (addingProductHere && editor != null) {
                CatalogProductEditor(editor = editor, viewModel = viewModel)
            }

            childProducts.forEach { product ->
                if (editor != null && editor.kind == CatalogEditorKind.PRODUCT && editor.editingLocalId == product.localId) {
                    CatalogProductEditor(
                        editor = editor,
                        viewModel = viewModel
                    )
                } else {
                    CatalogProductRow(
                        product = product,
                        onEdit = { viewModel.catalogStartEditProduct(product.localId) },
                        onDelete = { viewModel.catalogDeleteProduct(product.localId) }
                    )
                }
            }

            childCategories.forEach { child ->
                CatalogCategoryNode(category = child, state = state, viewModel = viewModel, depth = depth + 1)
            }

            if (addingSubcategoryHere && editor != null) {
                CatalogInlineNameEditor(editor = editor, viewModel = viewModel)
            }
        }
    }
}

@Composable
private fun CatalogCategoryHeader(
    name: String,
    expanded: Boolean,
    subcategoryCount: Int,
    productCount: Int,
    isSubGroup: Boolean,
    onToggle: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    val colors = AppTheme.current.colors
    val arrowRotation by animateFloatAsState(
        targetValue = if (expanded) 180f else 0f,
        animationSpec = tween(durationMillis = 200),
        label = "catalogChevronRotation"
    )
    val subtitle = if (isSubGroup) {
        "$productCount ${stringResource(SR.strings.products_count_suffix)}"
    } else {
        "$subcategoryCount ${stringResource(SR.strings.subcategories_count_suffix)} | " +
            "$productCount ${stringResource(SR.strings.products_count_suffix)}"
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(24.dp)
                .clip(CircleShape)
                .clickable(onClick = onToggle),
            contentAlignment = Alignment.Center
        ) {
            Image(
                painter = painterResource(SRC.images.combobox_arrow_down_green),
                contentDescription = null,
                modifier = Modifier
                    .size(16.dp)
                    .graphicsLayer { rotationZ = arrowRotation },
                colorFilter = ColorFilter.tint(colors.primary)
            )
        }
        Spacer(modifier = Modifier.width(p8))
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = p8)
        ) {
            Texts.TitleSmall(text = name.ifBlank { "-" }, maxLines = 1, color = colors.onSurface)
            Texts.BodySmall(
                text = subtitle,
                color = colors.onSurfaceVariant,
                maxLines = 1
            )
        }
        CatalogIconActionButtons(onEdit = onEdit, onDelete = onDelete)
    }
}

@Composable
private fun CatalogIconActionButtons(onEdit: () -> Unit, onDelete: () -> Unit) {
    val colors = AppTheme.current.colors
    Row(horizontalArrangement = Arrangement.spacedBy(p8)) {
        Box(
            modifier = Modifier
                .size(CatalogIconActionSize)
                .clip(CircleShape)
                .background(colors.primary)
                .clickable(onClick = onEdit),
            contentAlignment = Alignment.Center
        ) {
            Image(
                painter = painterResource(SRC.images.mol_edit_icon),
                contentDescription = stringResource(SR.strings.edit_category),
                modifier = Modifier.size(20.dp)
            )
        }
        Box(
            modifier = Modifier
                .size(CatalogIconActionSize)
                .clip(CircleShape)
                .background(colors.error)
                .clickable(onClick = onDelete),
            contentAlignment = Alignment.Center
        ) {
            Image(
                painter = painterResource(SRC.images.mol_trash_icon),
                contentDescription = stringResource(SR.strings.delete_category),
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

@Composable
private fun CatalogFilledActionButton(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val colors = AppTheme.current.colors
    CatalogGroupActionButton(
        label = label,
        onClick = onClick,
        modifier = modifier,
        backgroundColor = colors.primary,
        contentColor = colors.onPrimary
    )
}

@Composable
private fun CatalogContainerActionButton(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val colors = AppTheme.current.colors
    CatalogGroupActionButton(
        label = label,
        onClick = onClick,
        modifier = modifier,
        backgroundColor = colors.primaryContainer,
        contentColor = colors.primary
    )
}

@Composable
private fun CatalogGroupActionButton(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier,
    backgroundColor: Color,
    contentColor: Color
) {
    Row(
        modifier = modifier
            .heightIn(min = 40.dp)
            .clip(CatalogGroupActionButtonShape)
            .background(backgroundColor)
            .clickable(onClick = onClick)
            .padding(start = p16, end = p16, top = p12, bottom = 11.dp),
        horizontalArrangement = Arrangement.spacedBy(p10),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            painter = painterResource(SRC.images.ic_plus_icon),
            contentDescription = null,
            modifier = Modifier.size(16.dp),
            colorFilter = ColorFilter.tint(contentColor)
        )
        Texts.TitleSmall(text = label, color = contentColor, maxLines = 1)
    }
}

@Composable
private fun CatalogProductRow(
    product: CatalogProductDraft,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    val colors = AppTheme.current.colors
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = p8)
            .clip(RoundedCornerShape(CatalogCardCornerRadius))
            .background(colors.surfaceContainerLowest)
            .padding(horizontal = p16, vertical = p16),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = p12)
        ) {
            Texts.BodyLarge(
                text = product.name.ifBlank { "-" },
                maxLines = 1,
                color = colors.onSurface
            )
            if (product.grams > 0) {
                Texts.BodySmall(
                    text = stringResource(SR.strings.product_weight_grams, product.grams),
                    color = colors.onSurfaceVariant,
                    maxLines = 1
                )
            }
        }
        CatalogIconActionButtons(onEdit = onEdit, onDelete = onDelete)
    }
}

@Composable
private fun CatalogEmptyState() {
    val colors = AppTheme.current.colors
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = p32),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Image(
            painter = painterResource(SRC.images.ic_restaurant_menu),
            contentDescription = null,
            modifier = Modifier.size(48.dp),
            colorFilter = ColorFilter.tint(colors.primary)
        )
        Spacer(modifier = Modifier.height(p12))
        Texts.TitleSmall(text = stringResource(SR.strings.catalog_empty_title), textAlign = TextAlign.Center)
        Spacer(modifier = Modifier.height(p4))
        Texts.BodySmall(
            text = stringResource(SR.strings.catalog_empty_subtitle),
            color = colors.onSurfaceVariant,
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun CatalogInlineNameEditor(
    editor: CatalogEditorState,
    viewModel: MerchantRegistrationAddMerchantLocationDetailsViewModel
) {
    val colors = AppTheme.current.colors
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(p8)
    ) {
        DefaultCircleShapedTextField(
            modifier = Modifier.weight(1f),
            value = editor.nameInput,
            onValueChange = viewModel::catalogUpdateEditorName,
            label = stringResource(SR.strings.enter_group_name),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            isError = editor.nameError,
            errorText = ""
        )
        CatalogTextActionButton(
            text = stringResource(SR.strings.save),
            color = colors.primary,
            onClick = { viewModel.catalogSaveEditor() }
        )
        CatalogTextActionButton(
            text = stringResource(SR.strings.cancel),
            color = colors.error,
            onClick = { viewModel.catalogCancelEditor() }
        )
    }
}

@Composable
private fun CatalogTextActionButton(
    text: String,
    color: Color,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .height(CatalogActionButtonHeight)
            .clip(RoundedCornerShape(p28))
            .clickable(onClick = onClick)
            .padding(horizontal = p16),
        contentAlignment = Alignment.Center
    ) {
        Texts.LabelLarge(text = text, color = color, maxLines = 1)
    }
}

@Composable
private fun CatalogProductEditor(
    editor: CatalogEditorState,
    viewModel: MerchantRegistrationAddMerchantLocationDetailsViewModel
) {
    val colors = AppTheme.current.colors
    val state by viewModel.uiState.subscribeAsState()
    val isEditing = editor.editingLocalId != null
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(p24))
            .background(colors.surfaceContainerLowest)
            .padding(p20),
        verticalArrangement = Arrangement.spacedBy(p12)
    ) {
        Texts.TitleSmall(
            text = stringResource(if (isEditing) SR.strings.edit_product else SR.strings.add_product),
            color = colors.onSurface
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(p8)
        ) {
            DefaultCircleShapedTextField(
                modifier = Modifier.weight(1f),
                value = editor.nameInput,
                onValueChange = viewModel::catalogUpdateEditorName,
                label = stringResource(SR.strings.product_name),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                isError = editor.nameError,
                errorText = ""
            )
            DefaultCircleShapedTextField(
                modifier = Modifier.width(120.dp),
                value = editor.gramsInput,
                onValueChange = viewModel::catalogUpdateEditorGrams,
                label = stringResource(SR.strings.grams_short),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                isError = editor.gramsError,
                errorText = ""
            )
        }
        if (state.availableAllergens.isNotEmpty()) {
            Texts.BodyMedium(
                text = stringResource(SR.strings.allergens),
                color = colors.onSurfaceVariant
            )
            CatalogAllergenSelector(
                allergens = state.availableAllergens,
                selectedIds = editor.selectedAllergenIds,
                onToggle = viewModel::catalogToggleEditorAllergen
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            CatalogOutlinedFormButton(
                text = stringResource(SR.strings.save),
                color = colors.primary,
                onClick = { viewModel.catalogSaveEditor() }
            )
            Spacer(modifier = Modifier.width(p10))
            CatalogOutlinedFormButton(
                text = stringResource(SR.strings.cancel),
                color = colors.error,
                onClick = { viewModel.catalogCancelEditor() }
            )
        }
    }
}

@Composable
private fun CatalogOutlinedFormButton(
    text: String,
    color: Color,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .height(CatalogActionButtonHeight)
            .widthIn(min = 120.dp)
            .clip(RoundedCornerShape(p28))
            .border(p1, color, RoundedCornerShape(p28))
            .clickable(onClick = onClick)
            .padding(horizontal = p24),
        contentAlignment = Alignment.Center
    ) {
        Texts.LabelLarge(text = text, color = color, maxLines = 1)
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CatalogAllergenSelector(
    allergens: List<com.example.mystybag.sharedmodels.project.responseModels.nomenclatures.AllergenModel>,
    selectedIds: Set<Long>,
    onToggle: (Long) -> Unit
) {
    val colors = AppTheme.current.colors
    val chipShape = RoundedCornerShape(57.dp)
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(p8),
        verticalArrangement = Arrangement.spacedBy(p8)
    ) {
        allergens.forEach { allergen ->
            val selected = allergen.id in selectedIds
            Box(
                modifier = Modifier
                    .heightIn(min = 40.dp)
                    .clip(chipShape)
                    .then(
                        if (selected) {
                            Modifier.background(colors.primary)
                        } else {
                            Modifier
                                .background(colors.surface)
                                .border(p1, colors.onSurfaceVariant, chipShape)
                        }
                    )
                    .clickable { onToggle(allergen.id) }
                    .padding(horizontal = p20, vertical = p12),
                contentAlignment = Alignment.Center
            ) {
                Texts.TitleSmall(
                    text = allergen.name,
                    color = if (selected) colors.onPrimary else colors.secondaryContainer,
                    maxLines = 1
                )
            }
        }
    }
}

// endregion

